// Skilz Circle referral settings — admin-side mirror of the backend contract.
//
// Source of truth: skilzBackend app/models/platformPricing.js
// (DEFAULT_REFERRAL_SETTINGS, REFERRAL_SETTING_RULES, referralSettingError,
// validateReferralPatch). Keep these tables byte-for-byte in step with it.
//
// Units are the backend's: money in integer MINOR units (USD cents),
// percentages in integer basis points, durations in whole hours/days.
// Conversions to/from human units below use string + integer arithmetic only
// (never float multiply/divide) so 20% is always exactly 2000 bps.

export const DEFAULT_REFERRAL_SETTINGS = Object.freeze({
    rewardsEnabled: false,
    rewardPercentageBps: 2000,
    perDealCapMinor: 500,
    monthlyCapMinor: 5000,
    lifetimeCapMinor: 20000,
    qualifyingDealCount: 1,
    attributionWindowDays: 30,
    holdDurationHours: 48,
});

export const REFERRAL_SETTING_RULES = Object.freeze({
    rewardsEnabled: Object.freeze({ type: 'boolean' }),
    rewardPercentageBps: Object.freeze({ type: 'integer', min: 0, max: 10000 }),
    perDealCapMinor: Object.freeze({ type: 'integer', min: 0, max: 1000000 }),
    monthlyCapMinor: Object.freeze({ type: 'integer', min: 0, max: 10000000 }),
    lifetimeCapMinor: Object.freeze({ type: 'integer', min: 0, max: 100000000 }),
    // qualifyingDealCount is intentionally retained here (and in the defaults)
    // to mirror the backend contract, which still stores and accepts it. It is
    // deliberately NOT surfaced in the UI (absent from REFERRAL_NUMERIC_FIELDS):
    // it is vestigial — the earn path never reads it and, by design, rewards
    // every completed deal, bounded only by the per-deal/monthly/lifetime caps.
    qualifyingDealCount: Object.freeze({ type: 'integer', min: 1, max: 1000 }),
    attributionWindowDays: Object.freeze({ type: 'integer', min: 1, max: 3650 }),
    holdDurationHours: Object.freeze({ type: 'integer', min: 1, max: 8760 }),
});

export const EDITABLE_REFERRAL_SETTINGS = Object.freeze(Object.keys(REFERRAL_SETTING_RULES));

/** Exact mirror of the backend's non-coercing referralSettingError(). */
export function referralSettingError(key, value) {
    const rule = REFERRAL_SETTING_RULES[key];
    if (!rule) return `${key} is not a referral setting`;
    if (rule.type === 'boolean') {
        return typeof value === 'boolean' ? null : `${key} must be a boolean, got ${JSON.stringify(value)}`;
    }
    if (typeof value !== 'number' || !Number.isSafeInteger(value)) {
        return `${key} must be an integer, got ${JSON.stringify(value)}`;
    }
    if (value < rule.min || value > rule.max) {
        return `${key} must be between ${rule.min} and ${rule.max}, got ${value}`;
    }
    return null;
}

/**
 * Normalize the `referral` subdocument from GET /platformPricing the same way
 * the backend's getReferralSettings() reads it: each stored value is used only
 * if it passes the strict validator, otherwise the default applies. The kill
 * switch is ON only for a stored boolean `true` — anything else is OFF.
 */
export function normalizeReferralSettings(stored) {
    const src = stored && typeof stored === 'object' && !Array.isArray(stored) ? stored : null;
    const out = { ...DEFAULT_REFERRAL_SETTINGS };
    if (src) {
        for (const k of EDITABLE_REFERRAL_SETTINGS) {
            if (referralSettingError(k, src[k]) === null) out[k] = src[k];
        }
    }
    out.rewardsEnabled = Boolean(src && src.rewardsEnabled === true);
    return out;
}

// ── Exact unit conversions ──────────────────────────────────────────────────

const pad2 = (n) => String(n).padStart(2, '0');
const groupThousands = (digits) => digits.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

/** Integer hundredths → "W.FF" (no grouping). Used for bps→% and cents→$. */
function hundredthsToText(n) {
    return `${Math.floor(n / 100)}.${pad2(n % 100)}`;
}

/**
 * Strict "W" or "W.F" or "W.FF" → integer hundredths, or null when the text
 * is not a plain non-negative decimal with at most 2 places. No Number()
 * coercion of free text: '1e3', '-1', '0x10', '', '1.234' all return null.
 */
function textToHundredths(text) {
    if (typeof text !== 'string') return null;
    const m = /^(\d+)(?:\.(\d{1,2}))?$/.exec(text.trim());
    if (!m) return null;
    const whole = parseInt(m[1], 10);
    const frac = parseInt((m[2] || '').padEnd(2, '0'), 10);
    const value = whole * 100 + frac;
    return Number.isSafeInteger(value) ? value : null;
}

/** 2000 bps → "20.00" (percent). */
export const bpsToPercentText = (bps) => hundredthsToText(bps);
/** "20" / "20.00" → 2000 bps; null when malformed. */
export const percentTextToBps = (text) => textToHundredths(text);
/** 1000000 cents → "10000.00" (dollars). */
export const minorToDollarsText = (minor) => hundredthsToText(minor);
/** "10000" / "10000.00" → 1000000 cents; null when malformed. */
export const dollarsTextToMinor = (text) => textToHundredths(text);

/** Strict whole-number text → integer, or null. */
export function intTextToInt(text) {
    if (typeof text !== 'string') return null;
    const t = text.trim();
    if (!/^\d+$/.test(t)) return null;
    const value = parseInt(t, 10);
    return Number.isSafeInteger(value) ? value : null;
}

export const formatPercent = (bps) => `${bpsToPercentText(bps)}%`;
export const formatUsd = (minor) => {
    const [whole, frac] = minorToDollarsText(minor).split('.');
    return `$${groupThousands(whole)}.${frac}`;
};

// ── Field metadata (display order) ──────────────────────────────────────────

// Fields shown on the admin screen. qualifyingDealCount is deliberately
// omitted (see REFERRAL_SETTING_RULES). Set `inert: true` on a field only if
// the backend stores but never reads it — Pricing.js then renders
// INERT_FIELD_NOTE beside it. No field is inert today.
export const REFERRAL_NUMERIC_FIELDS = Object.freeze([
    {
        key: 'rewardPercentageBps',
        label: 'Reward percentage (%)',
        unit: 'percent',
        help: 'Share of the job-price fee paid to the inviter. 0% = no reward. Sent as basis points (100% = 10000 bps).',
    },
    {
        key: 'perDealCapMinor',
        label: 'Per-deal cap (USD)',
        unit: 'usd',
        help: 'Maximum reward for a single completed deal.',
    },
    {
        key: 'monthlyCapMinor',
        label: 'Monthly cap per inviter (USD)',
        unit: 'usd',
        help: 'Maximum an inviter can earn per calendar month.',
    },
    {
        key: 'lifetimeCapMinor',
        label: 'Lifetime cap per inviter (USD)',
        unit: 'usd',
        help: 'Maximum an inviter can ever earn.',
    },
    {
        key: 'holdDurationHours',
        label: 'Hold duration (hours)',
        unit: 'int',
        suffix: (n) => (n === 1 ? 'hour' : 'hours'),
        help: 'Hours an earned reward is held as PENDING before it is released.',
    },
    {
        key: 'attributionWindowDays',
        label: 'Attribution window (days)',
        unit: 'int',
        suffix: (n) => (n === 1 ? 'day' : 'days'),
        help: 'Days after an invite during which a sign-up is attributed to the inviter. The window is set when the invite is created and checked when the sign-up is attributed.',
    },
]);

export const INERT_FIELD_NOTE = 'Not enforced yet — this value is stored but the reward earn path does not read it.';

const fieldByKey = Object.fromEntries(REFERRAL_NUMERIC_FIELDS.map((f) => [f.key, f]));

/** Backend value → the text shown in an edit input. */
export function toFieldText(key, value) {
    const f = fieldByKey[key];
    if (f.unit === 'percent') return bpsToPercentText(value);
    if (f.unit === 'usd') return minorToDollarsText(value);
    return String(value);
}

/** Backend value → read-only human display. */
export function formatFieldValue(key, value) {
    const f = fieldByKey[key];
    if (f.unit === 'percent') return `${formatPercent(value)} (${value} bps)`;
    if (f.unit === 'usd') return formatUsd(value);
    return f.suffix ? `${value} ${f.suffix(value)}` : String(value);
}

function boundsMessage(key) {
    const f = fieldByKey[key];
    const { min, max } = REFERRAL_SETTING_RULES[key];
    if (f.unit === 'percent') return `Enter a percentage from ${formatPercent(min)} to ${formatPercent(max)}, with at most 2 decimal places.`;
    if (f.unit === 'usd') return `Enter a dollar amount from ${formatUsd(min)} to ${formatUsd(max)}, with at most 2 decimal places (cents).`;
    return `Enter a whole number from ${min} to ${max}.`;
}

/** Parse one edit-input text into the backend value. Returns { value } or { error }. */
export function parseFieldText(key, text) {
    const f = fieldByKey[key];
    let value;
    if (f.unit === 'percent') value = percentTextToBps(text);
    else if (f.unit === 'usd') value = dollarsTextToMinor(text);
    else value = intTextToInt(text);
    if (value === null) return { error: boundsMessage(key) };
    // Final gate: the exact backend validator (integer + bounds, no coercion).
    if (referralSettingError(key, value) !== null) return { error: boundsMessage(key) };
    return { value };
}

/** Backend-unit settings → editable draft (texts + boolean kill switch). */
export function toReferralDraft(settings) {
    const draft = { rewardsEnabled: settings.rewardsEnabled === true };
    for (const f of REFERRAL_NUMERIC_FIELDS) draft[f.key] = toFieldText(f.key, settings[f.key]);
    return draft;
}

/**
 * Build the PATCH `referral` object from the draft: only keys whose parsed
 * value differs from the last saved value. Returns { patch, errors } — when
 * errors is non-empty nothing may be sent.
 */
export function buildReferralPatch(draft, saved) {
    const base = saved || DEFAULT_REFERRAL_SETTINGS;
    const patch = {};
    const errors = {};
    if (typeof draft.rewardsEnabled !== 'boolean') {
        errors.rewardsEnabled = 'Kill switch must be on or off.';
    } else if (draft.rewardsEnabled !== base.rewardsEnabled) {
        patch.rewardsEnabled = draft.rewardsEnabled;
    }
    for (const f of REFERRAL_NUMERIC_FIELDS) {
        const res = parseFieldText(f.key, draft[f.key]);
        if (res.error) errors[f.key] = res.error;
        else if (res.value !== base[f.key]) patch[f.key] = res.value;
    }
    return { patch, errors };
}
